import { useForm, type UseFormReturn } from '@kanda-libs/ks-component-ts';
import { StringIndexedObject } from '@kanda-libs/ks-design-library';
import { actions, Customer } from '@kanda-libs/ks-frontend-services';
import { URLS } from 'config';
import { useApiError, useAppDispatch } from 'hooks';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { generatePath, useHistory } from 'react-router-dom';
import { useEnquiryContext } from '../../context';
import { formatEnquiryQuoteContactFormValues } from './helpers';

export interface EnquiryQuoteContactDetailsFormTerms {
  agreed_terms: {
    value: boolean;
  };
  ip: string;
  timestamp: string;
}

export interface EnquiryQuoteContactDetailsFormValues {
  customer: Customer;
  terms: EnquiryQuoteContactDetailsFormTerms;
}

export interface EnquiryQuoteContactDetailsFormHook {
  form: UseFormReturn<EnquiryQuoteContactDetailsFormValues>;
  onSubmit: (
    formValues: StringIndexedObject<EnquiryQuoteContactDetailsFormValues>,
  ) => void;
  isLoading: boolean;
  isSubmitting: boolean;
}

export default function useEnquiryQuoteContactDetailsForm(): EnquiryQuoteContactDetailsFormHook {
  const { id, enquiry, isLoading, isSubmitting, setIsSubmitting } =
    useEnquiryContext();

  const dispatch = useAppDispatch();
  const onApiError = useApiError('Unknown error submitting enquiry');
  const { push } = useHistory();

  const defaultValues = useMemo(() => {
    if (!enquiry) return undefined;
    const { enquiry_info: enquiryInfo } = enquiry;
    if (!enquiryInfo) return undefined;
    const { consumer } = enquiryInfo;
    if (!consumer) return undefined;
    return {
      customer: consumer,
    };
  }, [enquiry]);

  const form = useForm<EnquiryQuoteContactDetailsFormValues>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues,
  });

  const onSubmit = useCallback(
    (formValues: StringIndexedObject<EnquiryQuoteContactDetailsFormValues>) => {
      if (!id) return;
      const { consumer, consumerSignature } =
        formatEnquiryQuoteContactFormValues(formValues);
      setIsSubmitting(true);
      if (!consumer || !consumerSignature) return;
      dispatch(
        actions.postEnquiryConsumer({
          body: consumer,
          params: { id },
          onError: (err) => {
            setIsSubmitting(false);
            onApiError(err);
          },
          onSuccess: () => {
            dispatch(
              actions.postEnquiryConsumerSignature({
                body: consumerSignature,
                params: { id },
                onError: (err) => {
                  setIsSubmitting(false);
                  onApiError(err);
                },
                onSuccess: () => {
                  setIsSubmitting(false);
                  push(generatePath(URLS.enquiryQuoteUploadQuote, { id }));
                },
              }),
            );
          },
        }),
      );
    },
    [dispatch, id, onApiError, push, setIsSubmitting],
  );

  const { reset } = form;

  const initRef = useRef<boolean>(false);
  useEffect(() => {
    if (!enquiry || !defaultValues || initRef.current) return;
    initRef.current = true;
    reset(defaultValues);
  }, [enquiry, defaultValues, reset]);

  return {
    form,
    onSubmit,
    isLoading,
    isSubmitting,
  };
}
